/**
 * Module dependencies.
 */

import { ChevronDown } from 'lucide-react';
import { GREEN_DARK } from 'app/components/pages/landing/brand';
import { PageShell } from 'app/components/pages/landing/page-shell';
import React, { useState } from 'react';

/**
 * FAQ content.
 */

const categories: { items: { a: string; q: string }[]; title: string }[] = [
  {
    items: [
      {
        a: 'Contab brings your bank accounts, projects, crypto and notes together, so you can see your whole financial picture in one calm place.',
        q: 'What is Contab?'
      },
      {
        a: 'Use the Google button or your email and password on the sign-in page. If you do not have an account yet, an administrator can set one up for you.',
        q: 'How do I sign in?'
      },
      {
        a: 'Yes — Contab runs in any modern browser and the interface adapts to phones and tablets, so your finances travel with you.',
        q: 'Can I use Contab on my phone?'
      }
    ],
    title: 'Getting started'
  },
  {
    items: [
      {
        a: 'All traffic is encrypted in transit, passwords are stored only as salted hashes, and access to your data is tightly controlled. See the Security page for details.',
        q: 'Is my financial data secure?'
      },
      {
        a: 'No. We never sell your personal data and only share it with the providers needed to run the service. See our Privacy policy.',
        q: 'Do you sell my data?'
      },
      {
        a: 'Yes. You can export your data in a portable format and request deletion of your account at any time.',
        q: 'Can I export or delete my data?'
      }
    ],
    title: 'Security & privacy'
  },
  {
    items: [
      {
        a: 'Contab connects to banks through our integration partners. Once a bank is connected, its transactions flow in automatically — no manual imports.',
        q: 'Which banks can I connect?'
      },
      {
        a: 'Yes — you can follow your crypto providers, trades and transactions right next to the rest of your finances.',
        q: 'Does Contab track crypto?'
      },
      {
        a: 'Projects let you group income and expenses, track budgets and proposals, and manage suppliers end to end.',
        q: 'What are projects?'
      }
    ],
    title: 'Features'
  }
];

/**
 * FAQ accordion item.
 */

function FaqItem({ a, first, q }: { a: string; first: boolean; q: string }): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div style={first ? undefined : { borderTop: '1px solid rgba(228,228,231,0.7)' }}>
      <button
        aria-expanded={open}
        className={'flex w-full items-center justify-between gap-4 px-6 py-4 text-left'}
        onClick={() => setOpen(value => !value)}
        type={'button'}
      >
        <span className={'font-medium text-neutral-800'}>{q}</span>

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && <p className={'px-6 pb-5 pr-12 text-[15px] leading-relaxed text-neutral-600'}>{a}</p>}
    </div>
  );
}

/**
 * Export `FaqsPage` component.
 */

export function FaqsPage(): JSX.Element {
  return (
    <PageShell>
      <main className={'mx-auto w-full max-w-3xl flex-1 px-6 py-16 sm:py-20'}>
        <h1 className={"font-['Outfit'] text-4xl font-semibold tracking-[-0.03em] sm:text-5xl"}>
          {'Frequently asked questions'}
        </h1>

        <p className={'mt-4 text-lg text-neutral-500'}>{'Everything you need to know about Contab.'}</p>

        {categories.map(category => (
          <section className={'mt-10'} key={category.title}>
            <h2 className={"font-['Outfit'] text-xl font-semibold tracking-[-0.02em] text-neutral-900"}>
              {category.title}
            </h2>

            <div
              className={'mt-4 overflow-hidden rounded-2xl border border-white/60 backdrop-blur-xl'}
              style={{
                backgroundImage: 'linear-gradient(160deg, rgba(255,255,255,0.85), rgba(255,255,255,0.6))',
                boxShadow: '0 30px 70px -40px rgba(10,10,11,0.3), inset 0 1px 0 0 rgba(255,255,255,0.9)'
              }}
            >
              {category.items.map((item, index) => (
                <FaqItem a={item.a} first={index === 0} key={item.q} q={item.q} />
              ))}
            </div>
          </section>
        ))}

        {/* Contact */}
        <div className={'mt-12 rounded-2xl border border-white/60 bg-white/60 p-6 backdrop-blur'}>
          <h2 className={"font-['Outfit'] text-lg font-semibold tracking-[-0.02em]"}>{'Still need help?'}</h2>

          <p className={'mt-2 text-[15px] leading-relaxed text-neutral-600'}>
            {'Reach out to '}

            <a className={'font-medium'} href={'mailto:support@contab.app'} style={{ color: GREEN_DARK }}>
              {'support@contab.app'}
            </a>

            {' and our team will get back to you.'}
          </p>
        </div>
      </main>
    </PageShell>
  );
}
